import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { formatNum, apiErr } from "../../lib/utils";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { Check, FileText, ArrowRight, Star, Loader2, Sparkles, CreditCard } from "lucide-react";

const fmtMoney = (n) => `S$${formatNum(n || 0, { decimals: 0 })}`;

async function openPdf(url, filename, { download = false } = {}) {
  const r = await api.get(url, { responseType: "blob" });
  const u = URL.createObjectURL(r.data);
  if (download) {
    const a = document.createElement("a");
    a.href = u; a.download = filename; a.click();
  } else {
    window.open(u, "_blank");
  }
  setTimeout(() => URL.revokeObjectURL(u), 4000);
}

function priceLabel(s) {
  if (s.buy_type === "quote") return "Scoped quote";
  const suffix = s.billing_type === "recurring" ? `/${s.recurring_interval === "year" ? "yr" : s.recurring_interval === "quarter" ? "qtr" : "mo"}` : "";
  return `from ${fmtMoney(s.price_sgd)}${suffix}`;
}

export default function ServicesCatalogue({ canBuy, onPurchased }) {
  const [cats, setCats] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    api.get("/services/catalog").then((r) => setCats(r.data.categories || [])).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const packages = cats.find((c) => c.key === "packages");
  const otherCats = cats.filter((c) => c.key !== "packages");

  return (
    <div className="space-y-8" data-testid="services-catalogue">
      <p className="text-sm text-muted-foreground max-w-3xl">
        Expert help to get more from Lestari — implementation, ESG advisory, business value and ongoing managed services.
        Buy online and receive a professional Statement of Work for exactly what will be delivered. Prices shown before GST.
      </p>

      {!cats.length && <div className="py-12 text-center text-muted-foreground text-sm">Loading catalogue…</div>}

      {packages && (
        <section data-testid="section-packages">
          <div className="flex items-center gap-2 mb-1"><Star className="h-4 w-4 text-accent" /><h2 className="font-display text-xl font-bold">{packages.name}</h2></div>
          <p className="text-sm text-muted-foreground mb-4">{packages.blurb}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {packages.services.map((s) => (
              <div key={s.code} className="card-flat p-5 flex flex-col border-accent/30" data-testid={`service-card-${s.code}`}>
                <div className="font-display text-lg font-bold">{s.name}</div>
                <div className="font-mono text-2xl mt-1 text-accent">{fmtMoney(s.price_sgd)}</div>
                <p className="text-xs text-muted-foreground mt-2 flex-1">{s.proposition}</p>
                <ul className="text-[11px] text-muted-foreground mt-3 space-y-1">
                  {(s.includes || []).slice(0, 5).map((it, i) => (
                    <li key={i} className="flex gap-1.5"><Check className="h-3 w-3 text-[#00E65B] shrink-0 mt-0.5" />{it}</li>
                  ))}
                </ul>
                <Button variant="primary" size="sm" className="mt-4 w-full" onClick={() => setDetail(s.code)} data-testid={`view-${s.code}`}>
                  View & buy <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}

      {otherCats.map((c) => (
        <section key={c.key} data-testid={`section-${c.key}`}>
          <h2 className="font-display text-xl font-bold">{c.name}</h2>
          <p className="text-sm text-muted-foreground mb-4">{c.blurb}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {c.services.map((s) => (
              <button key={s.code} onClick={() => setDetail(s.code)} data-testid={`service-card-${s.code}`}
                className="card-flat p-4 text-left hover:border-accent/60 transition-colors flex flex-col">
                <div className="font-semibold text-sm">{s.name}</div>
                <p className="text-[11px] text-muted-foreground mt-1 flex-1 line-clamp-3">{s.proposition}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="font-mono text-sm text-accent">{priceLabel(s)}</span>
                  {s.buy_type === "quote"
                    ? <Badge variant="outline" className="text-[9px]">Quote</Badge>
                    : s.billing_type === "recurring" ? <Badge variant="outline" className="text-[9px]">Recurring</Badge> : null}
                </div>
              </button>
            ))}
          </div>
        </section>
      ))}

      {detail && (
        <ServiceDetailDialog code={detail} canBuy={canBuy} onClose={() => setDetail(null)}
          onNavigateMine={() => { setDetail(null); onPurchased?.(); }} />
      )}
    </div>
  );
}

function ServiceDetailDialog({ code, canBuy, onClose, onNavigateMine }) {
  const [svc, setSvc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [quoteMode, setQuoteMode] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    api.get(`/services/catalog/${code}`).then((r) => setSvc(r.data.service)).catch((e) => toast.error(apiErr(e)));
  }, [code]);

  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/services/purchase", { service_code: code, origin_url: window.location.origin });
      if (data.mode === "checkout" && data.url) { window.location.href = data.url; return; }
      toast.success("Managed service activated — your Statement of Work is ready.");
      onNavigateMine();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const requestQuote = async () => {
    setBusy(true);
    try {
      await api.post("/services/quote-request", { service_code: code, notes });
      toast.success("Quote requested — SusTek will send you a price to approve.");
      onNavigateMine();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const isRecurring = svc?.billing_type === "recurring";
  const interval = svc?.recurring_interval === "year" ? "yr" : svc?.recurring_interval === "quarter" ? "qtr" : "mo";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="service-detail-dialog">
        {!svc ? <div className="py-16 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{svc.name}</DialogTitle>
              <DialogDescription>{svc.proposition}</DialogDescription>
            </DialogHeader>

            <div className="flex items-center justify-between rounded-lg border border-border p-3 bg-surface/40">
              <div>
                <div className="label-eyebrow text-[9px]">Pricing</div>
                <div className="font-mono text-sm">{svc.price_display}</div>
              </div>
              <div className="text-right">
                {svc.buy_type === "quote"
                  ? <Badge variant="outline">Scoped quote</Badge>
                  : <div className="font-mono text-xl text-accent">{fmtMoney(svc.price_sgd)}{isRecurring ? <span className="text-xs text-muted-foreground">/{interval}</span> : ""}</div>}
                <div className="text-[10px] text-muted-foreground mt-0.5">before GST</div>
              </div>
            </div>

            {(svc.includes || []).length > 0 && (
              <Section title="What's included">
                {svc.includes.map((it, i) => <Bullet key={i}>{it}</Bullet>)}
              </Section>
            )}
            <Section title="Scope of services">{svc.scope.map((it, i) => <Bullet key={i}>{it}</Bullet>)}</Section>
            <Section title="Deliverables">{svc.deliverables.map((it, i) => <Bullet key={i}>{it}</Bullet>)}</Section>
            <div className="text-xs"><span className="label-eyebrow text-[9px]">Indicative timeline</span><div className="mt-0.5">{svc.timeline}</div></div>

            <button onClick={() => openPdf(`/services/catalog/${code}/sample-sow.pdf`, `SoW-sample-${code}.pdf`)}
              className="inline-flex items-center gap-1.5 text-xs text-accent hover:underline mt-1" data-testid="sample-sow-link">
              <FileText className="h-3.5 w-3.5" /> Preview a sample Statement of Work (PDF)
            </button>

            {!canBuy ? (
              <div className="text-xs text-muted-foreground border-t border-border/50 pt-3">
                Only your organisation's administrator can purchase services or request a quote.
              </div>
            ) : svc.buy_type === "quote" ? (
              quoteMode ? (
                <div className="border-t border-border/50 pt-3 space-y-2">
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="quote-notes"
                    placeholder="Tell us about your scope (entities, systems, timelines)…"
                    className="w-full text-sm rounded-md border border-border bg-transparent p-2 focus:outline-none focus:ring-1 focus:ring-accent" />
                  <Button variant="primary" className="w-full" disabled={busy} onClick={requestQuote} data-testid="submit-quote-btn">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Request scoped quote</>}
                  </Button>
                </div>
              ) : (
                <Button variant="primary" className="w-full mt-1" onClick={() => setQuoteMode(true)} data-testid="request-quote-btn">
                  <Sparkles className="h-4 w-4" /> Request a scoped quote
                </Button>
              )
            ) : (
              <Button variant="primary" className="w-full mt-1" disabled={busy} onClick={buy} data-testid="buy-now-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                  : isRecurring ? <>Activate · {fmtMoney(svc.price_sgd)}/{interval}</>
                  : <><CreditCard className="h-4 w-4" /> Buy now · {fmtMoney(svc.price_sgd)}</>}
              </Button>
            )}
            {svc.buy_type !== "quote" && !isRecurring && canBuy && (
              <p className="text-[11px] text-muted-foreground text-center">Secure Stripe checkout · your SoW is issued on payment.</p>
            )}
            {isRecurring && canBuy && (
              <p className="text-[11px] text-muted-foreground text-center">Billed on your Lestari invoice each {svc.recurring_interval} · cancellable.</p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="label-eyebrow text-[9px] mb-1">{title}</div>
      <ul className="space-y-1">{children}</ul>
    </div>
  );
}
function Bullet({ children }) {
  return <li className="flex gap-2 text-sm text-muted-foreground"><Check className="h-4 w-4 text-[#00E65B] shrink-0 mt-0.5" />{children}</li>;
}
